/**
 * Marketplace Metrics Card Component
 * Displays marketplace statistics and conversion funnel
 */

import React from 'react';
import { MarketplaceMetrics } from '../../types';
import { TrendingUp } from 'lucide-react';

interface MarketplaceMetricsCardProps {
  metrics: MarketplaceMetrics;
}

export const MarketplaceMetricsCard: React.FC<MarketplaceMetricsCardProps> = ({ metrics }) => {
  if (!metrics) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400">
        No marketplace data available
      </div>
    );
  }

  const conversionRate = metrics.conversionRate || 0;
  const conversionPercentage = Math.round(conversionRate * 100) / 100;

  return (
    <div className="space-y-6">
      {/* Metrics Grid */}
      <div className="grid grid-cols-2 gap-4">
        {/* Total Products */}
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-600 font-medium">Total Products</span>
            <div className="text-lg text-blue-600 font-bold">{metrics.totalProducts}</div>
          </div>
          <div className="text-xs text-gray-500">
            {metrics.activeProducts} active
          </div>
        </div>

        {/* Total Inquiries */}
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-600 font-medium">Total Inquiries</span>
            <div className="text-lg text-green-600 font-bold">{metrics.totalInquiries}</div>
          </div>
          <div className="text-xs text-gray-500">
            {(metrics as any).totalInquiriesContacted || 0} contacted
          </div>
        </div>
      </div>

      {/* Contacted Persons Breakdown */}
      {(metrics.totalInquiries > 0 || (metrics as any).totalInquiriesContacted > 0) && (
        <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
          <div className="text-xs text-gray-600 font-medium mb-3">📞 Contacted Persons Breakdown</div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-700">Total Inquiries (all statuses)</span>
              <span className="text-sm font-bold text-gray-900">{metrics.totalInquiries}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-700">Contacted Persons (status: contacted)</span>
              <span className="text-sm font-bold text-amber-600">{(metrics as any).totalInquiriesContacted || 0}</span>
            </div>
            <div className="border-t border-amber-200 pt-2 mt-2 flex justify-between items-center">
              <span className="text-xs text-gray-700 font-medium">Contact Rate</span>
              <span className="text-sm font-bold text-amber-700">
                {metrics.totalInquiries > 0 ? Math.round(((metrics as any).totalInquiriesContacted / metrics.totalInquiries) * 100) : 0}%
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Conversion Funnel */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-purple-600" />
          Conversion Funnel
        </h4>

        <div className="space-y-2">
          {/* Inquiries to Contacted */}
          {metrics.totalInquiries > 0 && (
            <div className="space-y-1">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-gray-700 font-medium">Inquiry → Contact</span>
                <span className="text-xs font-semibold text-gray-900">
                  {Math.round(((metrics as any).totalInquiriesContacted || 0) / metrics.totalInquiries * 100)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className="h-2 rounded-full bg-gradient-to-r from-purple-500 to-purple-600 transition-all duration-300"
                  style={{ width: `${Math.min(Math.round(((metrics as any).totalInquiriesContacted || 0) / metrics.totalInquiries * 100), 100)}%` }}
                />
              </div>
            </div>
          )}

          {/* Product listing rate */}
          {metrics.totalProducts > 0 && (
            <div className="space-y-1">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-gray-700 font-medium">Product Listing Rate</span>
                <span className="text-xs font-semibold text-gray-900">
                  {Math.round((metrics.activeProducts / metrics.totalProducts) * 100)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-300"
                  style={{ width: `${(metrics.activeProducts / metrics.totalProducts) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-100">
        <div className="text-xs text-gray-600 mb-2">Marketplace Health</div>
        <div className="flex items-baseline gap-2">
          <div className="text-2xl font-bold text-gray-900">
            {metrics.totalInquiries > 0 ? Math.round(((metrics as any).totalInquiriesContacted || 0) / metrics.totalInquiries * 100) : 0}%
          </div>
          <div className="text-xs text-gray-600">contact rate</div>
        </div>
        <div className="text-xs text-gray-500 mt-2">
          {(metrics as any).totalInquiriesContacted || 0} of {metrics.totalInquiries} inquiries contacted
        </div>
      </div>
    </div>
  );
};

export default MarketplaceMetricsCard;
